import { createSelector } from 'reselect';
import get from 'lodash/get';
import size from 'lodash/size';
import { joinedChannelsSelector } from '../../chatConnection/selectors';
import { lastUserIdSelector } from './settings';
import sortByKey from '../../../utils/sorting';
import connectToChatData from '../helpers/connectToChatData';
import { LAST_MESSAGE_SORTING } from '../../../constants/props';

export const reqChannelsSelector = state => state.board.reqChannels;
export const channelsListSelector = state => state.board.channels;

export const totalChannelsSelector = createSelector(
  reqChannelsSelector,
  request => get(request, 'meta.totalUsers', 0),
);

export const hasChannelsSelector = createSelector(
  channelsListSelector,
  channels => !!get(channels, 'length', 0),
);

export const sendingSelector = createSelector(
  reqChannelsSelector,
  request => get(request, 'sending') || false,
);

export const succeedSelector = createSelector(
  reqChannelsSelector,
  request => get(request, 'succeed') || false,
);

// loading states
export const loadingStateSelector = createSelector(
  sendingSelector,
  succeedSelector,
  (sending, succeed) => sending && !succeed,
);

export const loadingFirstPageSelector = createSelector(
  loadingStateSelector,
  hasChannelsSelector,
  (loading, hasChannels) => loading && !hasChannels,
);

export const loadingNextPageSelector = createSelector(
  loadingStateSelector,
  hasChannelsSelector,
  (loading, hasChannels) => loading && hasChannels,
);

// loaded states
export const loadedStateSelector = createSelector(
  sendingSelector,
  succeedSelector,
  (sending, succeed) => sending && !succeed,
);

export const loadedFirstPageSelector = createSelector(
  loadedStateSelector,
  hasChannelsSelector,
  (loading, hasChannels) => loading && !hasChannels,
);

export const loadedNextPageSelector = createSelector(
  loadedStateSelector,
  hasChannelsSelector,
  (loading, hasChannels) => loading && hasChannels,
);

// channels
// Returns sorted by lastMessageAt key array:
// [
//   {
//     contextUser: {},
//     lastMessageAt: '2018-05-01T11:00:41.488066',
//     subchannels: [{}, {}],
//     totalUnreadCount: 2, // api-chat-differences handling
//     unreadCount: 2, // api-chat-differences handling
//   },
// ]
// *Connect to chat data takes api array and returns initial data +:
// - if channel had any activity - chat unread / lastMessage data
// - else - api data about unreads and lastMessage
export const sortedChannelsSelector = createSelector(
  channelsListSelector,
  joinedChannelsSelector,
  (channels, joinedChannels) => {
    const connectedChannels = joinedChannels
      ? connectToChatData(channels, joinedChannels)
      : channels;

    return sortByKey(connectedChannels, LAST_MESSAGE_SORTING);
  },
);

// temp for context only channels available
export const channelsSelector = createSelector(
  channelsListSelector,
  channelsList =>
    channelsList.reduce((acc, channel) => {
      const { contextUser } = channel;
      const { id } = contextUser || {};

      acc[id] = channel;

      return acc;
    }, {}),
);

export const selectedChannelSelector = createSelector(
  channelsSelector,
  lastUserIdSelector,
  (channels, id) => get(channels, id) || {},
);

export const selectedChannelUserSelector = createSelector(
  selectedChannelSelector,
  channel => get(channel, 'contextUser') || {},
);

// Default chat to connect
export const defaultPatientIdSelector = createSelector(
  sortedChannelsSelector,
  sortedChannels => {
    // todo: manage chat without context
    return get(sortedChannels, [0, 'contextUser', 'id'], null);
  },
);

// Returns object with keys === patientId and values = object with keys === subchannelId
// {
//   1: { // patientId
//     123: { // channelId
//       channelId: 123,
//       channelName: 'provider_circle',
//       lastMessageAt: '2018-03-19T14:24:10.064000',
//       totalUnreadCount: 0,
//       type: 'system',
//     },
//     456: {
//       channelId: 456,
//       channelName: 'circles_circle',
//       lastMessageAt: '2018-03-19T14:24:10.064000',
//       totalUnreadCount: 2,
//       type: 'system',
//     },
//     ...
//   },
//   ...
// }
export const subChannelsSelector = createSelector(
  channelsListSelector,
  channels => {
    return channels.reduce((acc, channel) => {
      const { subchannels, contextChannel, contextUser } = channel;

      // TODO: manage contextChannels connection
      // if (contextChannel) {
      // acc.push(contextChannel);
      // }

      if (subchannels) {
        const { id } = contextUser || {};
        acc[id] = subchannels.reduce((result, subchannel) => {
          const { channelId } = subchannel;
          // eslint-disable-next-line no-param-reassign
          result[channelId] = subchannel;

          return result;
        }, {});
      }

      return acc;
    }, {});
  },
);

export const defaultFirstActiveChannel = createSelector(
  sortedChannelsSelector,
  channels => {
    const firstItem = get(channels[0], 'contextChannel', null);
    if (firstItem) {
      return get(firstItem, 'channelId');
    }
    return null;
  },
);

export const defaultFirstContextUser = createSelector(
  sortedChannelsSelector,
  channels => {
    const firstItem = get(channels[0], 'contextUser', null);
    if (firstItem) {
      return get(firstItem, 'id');
    }
    return null;
  },
);

export const channelsCountSelector = createSelector(
  channelsListSelector,
  channels => size(channels),
);



// WEBPACK FOOTER //
// ./src/redux/board/selectors/channels.js